import styles from './AssistedModeConditionalFields.module.css';

import { Field, FieldArray, Formik } from 'formik';
import React, { useEffect, useMemo } from 'react';

import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import BngField from 'components/bng/form/BngField';
import BngForm from 'components/bng/form/BngForm';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngIconButton from 'components/bng/ui/BngIconButton';
import BngCheckbox from 'components/bng/form/BngCheckbox';
import {
  buildComparisonOptions,
  checkNullCondition,
} from 'components/bng/pages/admin/structures/bimQuery/dialogs/AssistedModeFilterDialog';
import { bngYup } from 'components/bng/form/yup/BngYup';

const FormSchema = bngYup((yup) => {
  return yup.object({
    newField: yup.string().required().default(''),
    newFieldAlias: yup.string().required().default(''),
    conditionedField: yup.string().required().default(''),
    fieldsConditions: yup.array(
      yup.object({
        conditionType: yup.string().required().default(''),
        comparisonValue: yup
          .string()
          .when('conditionType', {
            is: (val) => val && !checkNullCondition(val),
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable(),
          })
          .default(''),
        result: yup.string().required().default(''),
      })
    ),
    useConditionedFieldValue: yup.boolean().default(false),
    elseValue: yup
      .string()
      .nullable()
      .default('')
      .when('useConditionedFieldValue', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
  });
});

export default function AssistedModeConditionalFields({
  closeModal,
  nodes,
  cases,
  fieldConfigs,
  onChange = _.noop,
  existingCase = {},
}) {
  const { t } = useTranslation();

  const comparisonOptions = buildComparisonOptions(t);
  const boolOptions = [
    { value: 'TRUE', label: t('assisted.mode.filter.true') },
    { value: 'FALSE', label: t('assisted.mode.filter.false') },
  ];

  const fields = useMemo(() => {
    return nodes.flatMap((node) => {
      const tableInfo = node.data.tableInfo;
      return tableInfo.fields.map((field) => ({
        ...field,
        displayName: tableInfo.alias ? `${tableInfo.alias}.${field.name}` : `${tableInfo.table}.${field.name}`,
      }));
    });
  }, [nodes]);

  const initialValues = useMemo(() => {
    return {
      ...FormSchema.default(),
      newField: existingCase.newField || '',
      newFieldAlias: existingCase.newFieldAlias || '',
      conditionedField: existingCase.conditionedField || '',
      fieldsConditions:
        existingCase.fieldsConditions && Array.isArray(existingCase.fieldsConditions)
          ? existingCase.fieldsConditions
          : [{ conditionType: '', comparisonValue: '', result: '' }],
      useConditionedFieldValue: existingCase.useConditionedFieldValue || false,
      elseValue: existingCase.elseValue || '',
    };
  }, [existingCase]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={FormSchema}
      onSubmit={(values) => {
        const cases = {
          newField: values.newField,
          newFieldAlias: values.newFieldAlias,
          conditionedField: values.conditionedField,
          fieldsConditions: values.fieldsConditions,
          useConditionedFieldValue: values.useConditionedFieldValue,
          elseValue: values.elseValue,
        };

        const fieldConfig = {
          caption: values.newFieldAlias,
          description: values.newField,
          dimensionType: 'Regular',
          fieldName: values.newField,
          fieldType: 'TEXT',
          filterOnly: false,
          formatString: 'Standard',
          georeference: false,
          hasAll: true,
          id: null,
          indexed: false,
          key: false,
          measure: true,
          measureAggregator: 'SUM',
          nodeId: null,
          orderFieldName: null,
          props: {
            metadata: {
              description: null,
              empty: true,
              functionality: [],
              primary: null,
              relevance: null,
              selectedLinks: [],
            },
          },
          regularField: false,
          selected: true,
          sourceFieldName: values.newField,
          timeDimension: false,
          valid: true,
          visible: true,
          visibleOnUi: true,
          isConditional: true,
        };

        onChange({ cases, fieldConfig, isDelete: false });
        closeModal();
      }}
    >
      {({ values, submitCount, setFieldValue, isSubmitting }) => {
        const selectedConditionedField = fields?.find((f) => f.displayName === values.conditionedField);
        const conditionedFieldType = selectedConditionedField?.type;

        useEffect(() => {
          if (values.useConditionedFieldValue && values.elseValue !== '') {
            setFieldValue('elseValue', '');
          }
        }, [values.useConditionedFieldValue]);

        useEffect(() => {
          values.fieldsConditions.forEach((condition, index) => {
            const isNullCondition = checkNullCondition(condition.conditionType);
            if (isNullCondition && condition.comparisonValue !== '') {
              setFieldValue(`fieldsConditions.${index}.comparisonValue`, '');
            }
          });
        }, [values.fieldsConditions]);

        return (
          <Dialog
            className={styles.conditionalFieldsContainer}
            title={`${t('conditional.fields')}`}
            onClose={closeModal}
            newDialogLayout={true}
            loading={isSubmitting}
          >
            <BngForm>
              <Dialog.Body className={styles.dialogBodyContainer}>
                <div className={styles.newFieldNameWrapper}>
                  <Field
                    name="newField"
                    label={t('new.field.name')}
                    className={`${styles.newFieldInput} ${styles.defaultInput}`}
                    component={BngField}
                    required
                  />
                  <Field
                    name="newFieldAlias"
                    label={t('newInMemoryVisualizationField')}
                    className={`${styles.newFieldInput} ${styles.defaultInput}`}
                    component={BngField}
                    required
                  />
                </div>
                <div>
                  <Field
                    name="conditionedField"
                    label={t('conditioned.field')}
                    className={styles.selectInput}
                    component={BngField}
                    inputComponent={BngSelectSearch}
                    clearable={false}
                    options={fields.map((f) => ({
                      value: f.displayName,
                      label: f.displayName,
                    }))}
                    onChange={(newFieldName) => {
                      setFieldValue('conditionedField', newFieldName);

                      const newField = fields.find((f) => f.displayName === newFieldName);
                      const isBool = newField?.type === 'BOOL';

                      const newDefaultCondition = {
                        conditionType: isBool ? '=' : '',
                        comparisonValue: '',
                        result: '',
                      };

                      setFieldValue('fieldsConditions', [newDefaultCondition]);
                    }}
                    required
                  />
                </div>

                <FieldArray name="fieldsConditions">
                  {({ push, remove }) => (
                    <>
                      <div className={styles.conditionsTitleWrapper}>
                        <div className={styles.conditionsTitle}>{t('conditions')}</div>
                        <BngIconButton
                          icon={'add_circle'}
                          className={styles.addButton}
                          onClick={() =>
                            push({
                              conditionType: conditionedFieldType === 'BOOL' ? '=' : '',
                              comparisonValue: '',
                              result: '',
                            })
                          }
                        />
                      </div>
                      <div className={styles.fieldsConditionsWrapper}>
                        <div className={styles.conditionsHeader}>
                          <div className={styles.conditionsHeaderItem}>{t('condition.type')}</div>
                          <div className={styles.conditionsHeaderItem}>{t('comparison.value')}</div>
                          <div className={styles.conditionsHeaderItem}>{t('result')}</div>
                        </div>
                        {values.fieldsConditions.map((condition, index) => {
                          const currentConditionType = condition.conditionType;
                          const isNullCondition = ['IS NULL', 'IS NOT NULL'].includes(currentConditionType);
                          const isBoolField = conditionedFieldType === 'BOOL';

                          return (
                            <div key={index} className={styles.conditionsBody}>
                              <Field
                                name={`fieldsConditions.${index}.conditionType`}
                                className={`${styles.conditionsSelect} ${styles.selectInput}`}
                                component={BngField}
                                inputComponent={BngSelectSearch}
                                withLabel={false}
                                disabled={isBoolField}
                                options={
                                  isBoolField
                                    ? comparisonOptions.bool
                                    : conditionedFieldType === 'NUMERIC' || conditionedFieldType === 'DATE'
                                    ? comparisonOptions.numeric
                                    : conditionedFieldType === 'TEXT'
                                    ? comparisonOptions.text
                                    : []
                                }
                                required
                              />
                              {isBoolField ? (
                                <Field
                                  name={`fieldsConditions.${index}.comparisonValue`}
                                  className={`${styles.conditionsSelect} ${styles.selectInput}`}
                                  component={BngField}
                                  inputComponent={BngSelectSearch}
                                  options={boolOptions}
                                  withLabel={false}
                                  required
                                />
                              ) : (
                                <Field
                                  name={`fieldsConditions.${index}.comparisonValue`}
                                  className={`${styles.conditionsSelect} ${styles.defaultInput}`}
                                  component={BngField}
                                  withLabel={false}
                                  disabled={isNullCondition}
                                  required={!isNullCondition}
                                />
                              )}
                              <Field
                                name={`fieldsConditions.${index}.result`}
                                className={`${styles.conditionsSelect} ${styles.defaultInput}`}
                                component={BngField}
                                withLabel={false}
                                required
                              />
                              <BngIconButton
                                icon={'close'}
                                onClick={() => {
                                  if (values.fieldsConditions.length > 1) {
                                    remove(index);
                                  } else {
                                    setFieldValue(`fieldsConditions.${index}`, {
                                      conditionType: isBoolField ? '=' : '',
                                      comparisonValue: '',
                                      result: '',
                                    });
                                  }
                                }}
                                className={styles.removeButton}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </FieldArray>
                <div className={styles.elseConditionWrapper}>
                  <div className={styles.elseHeader}>
                    <div className={styles.elseTitle}>{t('else')}</div>
                    <div className={styles.useConditionedField}>
                      <Field
                        name="useConditionedFieldValue"
                        asProps={{ label: t('conditioned.field.value') }}
                        withLabel={false}
                        className={styles.useConditionedField}
                        component={BngField}
                        inputComponent={BngCheckbox}
                      />
                    </div>
                  </div>
                  <Field
                    name={`elseValue`}
                    className={`${styles.elseValueInput} ${styles.defaultInput}`}
                    component={BngField}
                    withLabel={false}
                    disabled={values.useConditionedFieldValue}
                  />
                </div>
              </Dialog.Body>

              <Dialog.Footer
                className={existingCase && Object.keys(existingCase).length > 0 ? `${styles.dialogFooter}` : ''}
              >
                {existingCase && Object.keys(existingCase).length > 0 && (
                  <BngIconButton
                    icon={'delete'}
                    text={t('delete.field')}
                    className={styles.deleteTableButton}
                    onClick={() => {
                      if (existingCase?.newField) {
                        const updatedCases = cases.filter(
                          (caseItem) =>
                            !(
                              caseItem.newField === existingCase.newField &&
                              caseItem.newFieldAlias === existingCase.newFieldAlias
                            )
                        );

                        const updatedFieldConfigs = fieldConfigs.filter(
                          (fieldConfig) =>
                            !(
                              fieldConfig.fieldName === existingCase.newField &&
                              fieldConfig.caption === existingCase.newFieldAlias &&
                              fieldConfig.isConditional === true
                            )
                        );

                        onChange({
                          cases: updatedCases,
                          fieldConfigs: updatedFieldConfigs,
                          isDelete: true,
                        });
                      }

                      closeModal();
                    }}
                  />
                )}
                <DefaultDialogActions
                  closeModal={closeModal}
                  submitCount={submitCount}
                  okLabel="apply"
                  cancelLabel="cancel"
                />
              </Dialog.Footer>
            </BngForm>
          </Dialog>
        );
      }}
    </Formik>
  );
}
